/**
 * Toaster — render del stack de toasts top-right.
 *
 * Lee `useToastStore.toasts` y para cada uno arranca un timer de
 * auto-dismiss respetando `duration`. El user también puede cerrarlos
 * manualmente con el botón ×.
 *
 * Mount único en `RootLayout` para que esté disponible globalmente.
 *
 * Estilos por `kind` usan los tokens semánticos de marca (Doc 18 §2.4):
 *   success  → official
 *   review   → review
 *   critical → critical
 *   info     → brand
 */

import { useEffect } from 'react'
import {
  AlertCircle,
  CheckCircle2,
  Info,
  TriangleAlert,
  X,
} from 'lucide-react'

import { useToastStore, type Toast, type ToastKind } from '../stores/toastStore'

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts)

  if (toasts.length === 0) return null

  return (
    <div
      role="region"
      aria-label="Notifications"
      className="fixed top-4 right-4 z-60 flex flex-col gap-2 max-w-sm pointer-events-none"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  )
}

function ToastItem({ toast }: { toast: Toast }) {
  const dismiss = useToastStore((s) => s.dismissToast)

  // Auto-dismiss con duration > 0. Sticky cuando duration === 0.
  useEffect(() => {
    if (toast.duration <= 0) return
    const handle = window.setTimeout(() => {
      dismiss(toast.id)
    }, toast.duration)
    return () => window.clearTimeout(handle)
  }, [toast.id, toast.duration, dismiss])

  const Icon = ICON_BY_KIND[toast.kind]
  const styles = STYLE_BY_KIND[toast.kind]

  return (
    <div
      role="status"
      aria-live={toast.kind === 'critical' ? 'assertive' : 'polite'}
      className={`pointer-events-auto rounded-md border shadow-sm flex items-start gap-3 px-4 py-3 ${styles.container}`}
    >
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${styles.icon}`} />
      <p className="font-body text-sm flex-1 text-ink">{toast.message}</p>
      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        aria-label="Cerrar"
        className="text-ink-muted hover:text-ink transition-colors shrink-0"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

const ICON_BY_KIND: Record<ToastKind, typeof CheckCircle2> = {
  success: CheckCircle2,
  info: Info,
  review: TriangleAlert,
  critical: AlertCircle,
}

const STYLE_BY_KIND: Record<
  ToastKind,
  { container: string; icon: string }
> = {
  success: {
    container: 'bg-official-soft border-official/30',
    icon: 'text-official',
  },
  info: {
    container: 'bg-brand-soft border-brand/30',
    icon: 'text-brand',
  },
  review: {
    container: 'bg-review-soft border-review/30',
    icon: 'text-review',
  },
  critical: {
    container: 'bg-critical-soft border-critical/40',
    icon: 'text-critical',
  },
}
